import { computed, ref, unref } from "vue"
import { useEmailParts } from "~/composables/useEmailParts"
import { useImageUrl } from "~/composables/useImageUrl"
import { normalizeSections, sectionCells, isRawHtmlEmail, rawHtmlFromSections } from "~/composables/useEmailSections"
import { useCssInline } from "~/composables/useCssInline"
import type { EmailT, EmailGroupT } from "~/types"
import type { MaybeRef } from "vue"

export async function useEmailBuilder({email, group, isPreview = false, inline = false}: {
    email: MaybeRef<EmailT>
    group: MaybeRef<EmailGroupT>
    isPreview?: boolean
    /** Inline <style>/class CSS into attributes (for export/send — email-client safe). */
    inline?: boolean
}) {
    const { inlineCss } = useCssInline()

    const images = ref(new Set<string>())
    const emailValue = unref(email)
    const groupValue = unref(group)

    if (!groupValue) {
        throw new Error("Group is required")
    }

    if (!emailValue) {
        throw new Error("Email is required")
    }

    // Safely set linkParams with nullish coalescing
    if (emailValue && groupValue) {
        emailValue.linkParams = emailValue.linkParams || ( groupValue.linkParams || "" )
    }

    // Get wrapper alias with safe fallbacks
    const wrapperAlias = emailValue?.wrapper || ( groupValue?.wrapper || "default" )

    // Get email parts from API
    const {getEmailPart} = await useEmailParts()
    let emailWrapper = await getEmailPart(wrapperAlias)

    // Guard on the template, not just the object. A missing part can come back as a
    // truthy `{data:null, error}` envelope (withSupabase turns thrown errors into a
    // 200), which passes an `if (!emailWrapper)` check and then blows up later on
    // `.template.replace(...)`.
    if (!emailWrapper?.template) {
        console.error(`Email wrapper not found (or has no template) for alias: ${wrapperAlias}`)
        // Provide a fallback to avoid errors
        emailWrapper = await getEmailPart("default")

        if (!emailWrapper?.template) {
            console.error("Failed to load any email parts")
            // Create a minimal default wrapper as last resort
            emailWrapper = {
                alias: "default",
                props: {content: {type: "String", required: true}},
                template: "###CONTENT###",
                defaultVars: {}
            }
        }
    }

    // Get template default vars if available
    const templateDefaultVars = emailWrapper?.defaultVars || {}

    // Combine variables with email-specific vars taking precedence:
    // 1. Template defaults (lowest priority)
    // 2. Group variables (override template defaults)
    // 3. Email variables (highest priority, override group vars)
    const combinedVars = computed(() => {
        const baseVars = groupValue.vars || {}
        const emailVars = emailValue.vars || {}
        return {...templateDefaultVars, ...baseVars, ...emailVars}
    })

    function appendParams(url: string, params: string): string {
        if (!params) return url

        const hasParams = url.includes("?")
        const separator = hasParams ? "&" : "?"
        return `${url}${separator}${params}`
    }

    function textStyleAttr(style?: TextStyleT): string {
        if (!style) return ""
        const parts: string[] = []
        if (style.align) parts.push(`text-align:${style.align}`)
        if (style.bold) parts.push("font-weight:700")
        if (style.italic) parts.push("font-style:italic")
        if (style.color) parts.push(`color:${style.color}`)
        return parts.length ? ` style="${parts.join(";")}"` : ""
    }

    function buildTextSection(section: EmailSectionT): string {
        const styleAttr = textStyleAttr(section.style)
        let html = ""
        if (section.heading) html += `<h1${styleAttr}>${section.heading}</h1>`
        if (section.body) html += `<p${styleAttr}>${section.body}</p>`
        return html
    }

    function buildSpacerSection(section: EmailSectionT): string {
        const h = section.height ?? 24
        return `<div style="height:${h}px;line-height:${h}px;font-size:1px;">&nbsp;</div>`
    }

    function buildButtonSection(section: EmailSectionT): string {
        const text = section.buttonText?.trim()
        if (!text) return ""
        const style = section.buttonStyle || {}
        const align = style.align || "center"
        const bg = style.background || "#151e6d"
        const color = style.color || "#ffffff"
        const radius = style.radius ?? 6
        const href = section.buttonUrl
            ? appendParams(section.buttonUrl, unref(email)?.linkParams || "")
            : "#"
        // Bulletproof, email-safe button: a table cell with a padded anchor (no VML for
        // simplicity — degrades to a styled link in Outlook, which is acceptable here).
        return (
            `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;">` +
            `<tr><td align="${align}" style="padding:8px 0;">` +
            `<a href="${href}" target="_blank" style="display:inline-block;background:${bg};color:${color};` +
            `font-family:Arial,sans-serif;font-size:15px;font-weight:700;text-decoration:none;` +
            `padding:13px 26px;border-radius:${radius}px;">${text}</a>` +
            `</td></tr></table>`
        )
    }

    function buildDividerSection(section: EmailSectionT): string {
        const color = section.dividerColor || "#dddddd"
        const thickness = section.dividerThickness ?? 1
        return (
            `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;">` +
            `<tr><td style="padding:8px 0;"><div style="border-top:${thickness}px solid ${color};font-size:0;line-height:0;">&nbsp;</div></td></tr></table>`
        )
    }

    // Raw author-written HTML. Variables ({{var}} / ###var###) are substituted so custom
    // blocks can reference the same vars as the wrapper. CSS inlining (for <style>/classes)
    // is applied to the whole document after assembly — see the juice pass in buildHTML.
    function buildHtmlSection(section: EmailSectionT): string {
        let html = section.html || ""
        html = html.replace(/\{\{([^}]+)\}\}/g, (m: string, v: string) => combinedVars.value[v.trim()] ?? m)
        html = html.replace(/###([^#]+)###/g, (m: string, v: string) => combinedVars.value[v.trim()] ?? m)
        return html
    }

    /**
     * Render an image band. A single cell renders as a stacked, full-width image
     * (legacy behaviour). Multiple cells render side-by-side as an email-safe table
     * row — this is the new vertical-split band.
     */
    function buildImageBand(cells: EmailImageT[], sectionNumber: number): string {
        if (cells.length === 0) return ""

        if (cells.length === 1) {
            return buildImage(cells[0]!, sectionNumber, 1, {width: 600})
        }

        // Treat each cell's stored width as a proportion WEIGHT (the slicer stores the raw
        // cut width). Percentages are computed directly from the weights — exact, summing
        // to 100% — so the cells are perfectly proportional and share a uniform height (no
        // rounding-induced height hairline). Column-width attrs (px, for Outlook) are the
        // only rounded values. No max-width on the images, so each fills its cell exactly.
        const weights = cells.map((c) => (c.width && c.width > 0 ? c.width : 1))
        const sum = weights.reduce((a, b) => a + b, 0) || 1
        const attrW = weights.map((w) => Math.round((600 * w) / sum))
        attrW[attrW.length - 1] = (attrW[attrW.length - 1] || 0) + (600 - attrW.reduce((a, b) => a + b, 0))

        let html = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="600" style="width:100%;max-width:600px;border-collapse:collapse;font-size:0;line-height:0;"><tr>`
        cells.forEach((cell, index) => {
            const pct = ((weights[index]! / sum) * 100).toFixed(4)
            html += `<td width="${attrW[index]}" valign="top" style="width:${pct}%;font-size:0;line-height:0;">`
            html += buildImage(cell, sectionNumber, index + 1, {width: attrW[index]!})
            html += `</td>`
        })
        html += `</tr></table>`
        return html
    }

    async function buildHTML(): Promise<string> {
        let innerHTML = ""

        // Raw-HTML mode: the whole body is author-written HTML (bypasses the section model).
        // Detected either from the in-editor flag or the persisted `raw` section encoding.
        const rawMode = emailValue.htmlMode || isRawHtmlEmail(emailValue.sections)
        if (rawMode) {
            const rawBody = emailValue.htmlMode ? (emailValue.rawHtml || '') : rawHtmlFromSections(emailValue.sections)
            innerHTML = buildHtmlSection({ type: 'html', html: rawBody })
            // Fall back to a bare content slot when the wrapper part has no template
            // (e.g. a DB-loaded wrapper whose HTML lives under a different field),
            // so raw-HTML emails always render instead of throwing.
            let wrapper = emailWrapper?.template || "###CONTENT###"
            wrapper = wrapper.replace(/\{\{([^}]+)\}\}/g, (m: string, v: string) => combinedVars.value[v] || m)
            wrapper = wrapper.replace(/###([^#]+)###/g, (m: string, v: string) => combinedVars.value[v] || m)
            let out = wrapper.replace("###CONTENT###", innerHTML)
            if (inline) out = await inlineCss(out)
            return out
        }

        // Render one section tree to HTML. Consecutive image bands are grouped into a single
        // border-collapsed table (one row per band) so horizontal seams tile gap-free.
        function renderSections(secs: EmailSectionT[]): string {
            let out = ""
            let i = 0
            while (i < secs.length) {
                const section = secs[i]!

                if (section.type === "image") {
                    let table = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="600" style="width:100%;max-width:600px;border-collapse:collapse;font-size:0;line-height:0;">`
                    while (i < secs.length && secs[i]!.type === "image") {
                        const n = i + 1
                        const band = buildImageBand(sectionCells(secs[i]!), n)
                        const cell = isPreview ? `<div class="email-section" data-section="${n}">${band}</div>` : band
                        table += `<tr><td style="padding:0;font-size:0;line-height:0;">${cell}</td></tr>`
                        i++
                    }
                    table += `</table>`
                    out += table
                    continue
                }

                const sectionNumber = i + 1
                let sectionContent = ""
                if (section.type === "text") {
                    sectionContent += buildTextSection(section)
                } else if (section.type === "spacer") {
                    sectionContent += buildSpacerSection(section)
                } else if (section.type === "button") {
                    sectionContent += buildButtonSection(section)
                } else if (section.type === "divider") {
                    sectionContent += buildDividerSection(section)
                } else if (section.type === "html") {
                    sectionContent += buildHtmlSection(section)
                }

                if (isPreview && sectionContent) {
                    out += `<div class="email-section" data-section="${sectionNumber}">${sectionContent}</div>`
                } else {
                    out += sectionContent
                }
                i++
            }
            return out
        }

        // Separate mobile layout: `sections` is desktop, `mobileSections` is mobile. Tolerant
        // read so the server-side builder (raw DB row) sees the snake_case column too.
        const mobileRaw = emailValue.mobileSections ?? (emailValue as { mobile_sections?: EmailSectionT[] }).mobile_sections
        const mobileSecs = normalizeSections(mobileRaw)

        if (mobileSecs.length > 0) {
            // Two trees, each toggled by the wrapper's .for-desktop / .for-mobile classes at
            // 640px. Mirrors buildImage's per-image reveal, hoisted to the whole tree: desktop
            // gets the class + inline display fallback; mobile adds the mso reveal + mso-hide:all
            // so Outlook (which ignores @media) falls through to desktop.
            const desktopBlock = `<div class="for-desktop" style="display:none;">${renderSections(normalizeSections(emailValue.sections))}</div>`
            const mobileBlock = `<!--[if mso]><!--><div class="for-mobile" style="display:block;mso-hide:all;">${renderSections(mobileSecs)}</div><!--<![endif]-->`
            innerHTML += desktopBlock + mobileBlock
        } else {
            innerHTML += renderSections(normalizeSections(emailValue.sections))
        }

        // Get wrapper template and replace any variables. Mirrors the raw-HTML
        // branch's `emailWrapper?.template || "###CONTENT###"` — the previous
        // non-null assertion here asserted away a value that really can be missing.
        let wrapperTemplate = emailWrapper?.template || "###CONTENT###"

        // Process template variables in two forms:
        // 1. {{var_name}} style
        const varRegex = /\{\{([^}]+)\}\}/g
        wrapperTemplate = wrapperTemplate.replace(varRegex, (match, varName) => {
            return combinedVars.value[varName] || match // Keep original if variable not found
        })

        // 2. ###var_name### style (legacy)
        const legacyVarRegex = /###([^#]+)###/g
        wrapperTemplate = wrapperTemplate.replace(legacyVarRegex, (match, varName) => {
            return combinedVars.value[varName] || match // Keep original if variable not found
        })

        // Fallback toggle CSS: in two-tree mode, if the wrapper doesn't already define the
        // .for-desktop / .for-mobile swap (e.g. the bare `default` wrapper), inject it so the
        // layout still switches at 640px. Harmless duplicate when the wrapper already has it.
        if (mobileSecs.length > 0 && !wrapperTemplate.includes(".for-desktop")) {
            // !important on the base rules so they win over the inline display fallback after
            // the juice inline pass (export/send) — matching how the real wrappers are authored.
            // Keep the space before !important: Outlook drops the whole declaration without it.
            const toggleCss = `<style>.for-desktop{display:block !important}.for-mobile{display:none !important}@media only screen and (max-width:640px){.for-desktop{display:none !important}.for-mobile{display:block !important}}</style>`
            innerHTML = toggleCss + innerHTML
        }

        let finalHtml = wrapperTemplate.replace("###CONTENT###", innerHTML)
        if (inline) finalHtml = await inlineCss(finalHtml)
        return finalHtml
    }

    function buildImage(
        image: EmailImageT,
        sectionNumber: number,
        imageIndex: number,
        opts: { width?: number; trailingBreak?: boolean } = {},
    ): string {
        const {width: widthOverride} = opts
        let imageSrc = image.src
        let sizes = ["full"] // desktop, mobile
        if (image.src.includes("$SIZE")) {
            sizes = ["desktop", "mobile"]
        }

        let imgHTML = ""

        // Add preview wrapper if in preview mode
        if (isPreview && sectionNumber && imageIndex) {
            const imageLink = image.link || (image.linkVar && combinedVars.value[image.linkVar]) || ''
            imgHTML += `<div class="email-image" data-section="${sectionNumber}" data-image="${imageIndex}" data-link="${imageLink}">`
        }

        const linkParams = unref(email)?.linkParams || ""
        if (image.link) {
            // if there is a link, wrap the image in an anchor tag
            imgHTML += `<a href="${appendParams(image.link, linkParams)}">`
        } else if (image.linkVar) {
            // if there is a linkVar, wrap the image in an anchor tag with a variable
            // Use combinedVars to check email vars first, then fall back to group vars
            const baseUrl = combinedVars.value[image.linkVar] || "#"
            imgHTML += `<a href="${appendParams(baseUrl, linkParams)}">`
        }

        // add the image for each size
        for (const size of sizes) {

            // if the size is mobile, add a conditional comment for Outlook
            if (size === "mobile") {
                imgHTML += `<!--[if mso]><!-->`
            }

            // replace the $SIZE with the size
            imageSrc = image.src.replace("$SIZE", size)

            let imagePath = `${imageSrc}`
            if (!imagePath.startsWith("http")) {
                // Use our image URL utility to get the correct URL for the current environment
                const {getImageUrl} = useImageUrl()
                // Add timestamp to prevent caching during preview
                imagePath = getImageUrl(unref(group).alias, unref(email).alias, imagePath, true)
            }

            images.value.add(imagePath)

            // Outlook column width (px). widthOverride wins for cells so the raw stored
            // width can be a proportion weight (see buildImageBand).
            const width = widthOverride || image.width || 600

            // Block, full-width-of-cell image with NO max-width: the image always fills its
            // cell exactly (no sub-pixel sliver from a px cap), removing slice seams.
            const display = size === "desktop" ? "none" : "block"

            imgHTML += `<img src="${imagePath}" alt="${image.alt || imageSrc}" class="for-${size}" width="${width}" style="display:${display};width:100%;border:0;outline:none;"`

            // add the height if it is provided
            if (image.height) {
                imgHTML += ` height="${image.height}"`
            }

            // close the image tag
            imgHTML += `>`

            // if the size is mobile, close the conditional comment for Outlook
            if (size === "mobile") {
                imgHTML += `<!--<![endif]-->`
            }

        }

        if (image.link || image.linkVar) {
            imgHTML += `</a>`
        }

        // Close preview wrapper if in preview mode
        if (isPreview && sectionNumber && imageIndex) {
            imgHTML += `</div>`
        }

        // No trailing <br>: images are display:block, so bands stack seamlessly.
        return imgHTML
    }


    const html = await buildHTML()

    return {
        html,
        images
    }
}
